import { ActionIcon, Button, Group, TextInput, Text } from "@mantine/core";
import { Search, X } from "lucide-react";

import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import Head from "../../components/common/Head";
import ContentTable from "../../components/movies/Table";
import useValidate from "../../hooks/useValidate";
import { getAllSeries } from "../../utils/api";
import useStore from "../../utils/store";



const Series = ({ status }) => {
  const user = useStore((state) => state.user);
  const [series, setSeries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = React.useState(
    searchParams.get("search") || ""
  );
  const [inputText, setInputText] = React.useState(
    searchParams.get("search") || ""
  );

  const [pagination, setPagination] = React.useState({
    current: parseInt(searchParams.get("page") || "1"),
    pageSize: 10,
  });

  useValidate();
  const getAllSeriesData = async () => {
    setLoading(true);
    const [success, response] = await getAllSeries(
      pagination.current,
      pagination.pageSize,
      status,
      searchText
    );
    if (success) setSeries(response);
    setLoading(false);
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize: pageSize || pagination.pageSize });
    setSearchParams({
      page: page.toString(),
      ...(searchText && { search: searchText }),
    });
  };

  const handleSearch = (value) => {
    setSearchText(inputText);
    setPagination({ current: 1, pageSize: 10 });
    setSearchParams({
      page: "1",
      ...(inputText && { search: inputText }),
    });
  };

  const handleSearchClear = () => {
    setInputText("");
    setSearchText("");
    setPagination({ current: 1, pageSize: 10 });
    setSearchParams({
      page: "1",
    });
  };

  React.useEffect(() => {
    getAllSeriesData();
  }, [pagination.current, pagination.pageSize, status, searchText]);

  // Add permission check based on status
  const hasPermission = React.useMemo(() => {
    if (status === "Pending") return user?.getPendingSeries;
    if (status === "Approved") return user?.getAllSeries;
    if (status === "Rejected") return user?.getRejectedSeries;
    return user?.getAllSeries; // fallback to general permission
  }, [user, status]);

  // Add edit/delete permissions based on status
  const getPermissions = React.useMemo(() => {
    const permissions = {
      updateSeries: user?.updateSeries || false,
      deleteSeries: user?.deleteSeries || false,
      isPublishAccess: user?.isPublishAccess || false,
      addSeries: user?.addSeries || false,
    };

    if (status === "Pending") {
      permissions.updateSeries = user?.editPendingSeries || false;
      permissions.deleteSeries = user?.deletePendingSeries || false;
    } else if (status === "Approved") {
      permissions.updateSeries = user?.updateSeries || false;
      permissions.deleteSeries = user?.deleteSeries || false;
    } else if (status === "Rejected") {
      permissions.updateSeries = user?.editRejectedSeries || false;
      permissions.deleteSeries = user?.deleteRejectedSeries || false;
    }

    return permissions;
  }, [user, status]);

  return (
    <div className="flex flex-col gap-4">
      
      <Head
        title={`${status || ""} Series`}
        customButton={
          getPermissions.addSeries ? (
            <Link to="/series/add">
              <Button>Add Series</Button>
            </Link>
          ) : null
        }
      />

      {/* Search Input */}
      <div className="flex justify-start mb-4">
        <Group gap="xs">
          <TextInput
            placeholder="Search series by name, description, or other details..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            rightSection={
              inputText ? (
                <ActionIcon variant="subtle" color="gray" onClick={handleSearchClear}>
                  <X size={14} />
                </ActionIcon>
              ) : null
            }
            style={{ width: 400 }}
          />
          <ActionIcon size="lg" variant="filled" onClick={handleSearch}>
            <Search size={16} />
          </ActionIcon>
        </Group>
      </div>

      {!hasPermission ? (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Text type="secondary">
            You don't have permission to view {status?.toLowerCase() || ""}{" "}
            series
          </Text>
        </div>
      ) : (
        <ContentTable
          data={series?.data}
          loading={loading}
          pagination={{
            total: series?.metadata?.totalData,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          series
          reload={getAllSeriesData}
          permissions={getPermissions}
          currentPage={pagination.current}
          seasonButton={(id) => (
            <Link to={`/seasons/${id}`}>
              <Button>Seasons</Button>
            </Link>
          )}
          exportCsvData={series?.data}
          exportCsvFilename={`series-${status || "all"}-${new Date().toISOString().slice(0, 10)}.csv`}
        />
      )}
    </div>
  );
};

export default Series;
